'use client';

import React, { useState, useRef, useEffect } from 'react';
import useOutsideClick from '@/hooks/useOnClickOutside';
import useBodyScrollLock from '@/hooks/useBodyScrollLock';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import BupiLinkButton from '@/components/atoms/linkButton';
import BupiLink from '@/components/atoms/link';
import AgoraButton from '@/components/atoms/agoraButton';
import Image from '@/components/atoms/image';
import { HeaderProps } from '@/page-builder/global/mappers/header.mapper';
import { useTranslations } from 'next-intl';
import { Link, useRouter } from '@/i18n/navigation';

export const NavigationBar = ({ menuItems, logo, ctaButton }: HeaderProps) => {
  const t = useTranslations('header');
  
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const submenuRef = useRef<HTMLDivElement>(null);
  const { isDesktop } = useMediaQuery();

  const router = useRouter();
  const navigationMenus = menuItems;
  
  useOutsideClick(submenuRef as any, () => setActiveSubmenu(null));
  useBodyScrollLock(activeSubmenu !== null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && activeSubmenu) {
        setActiveSubmenu(null);
        const menuButton = document.querySelector(`[aria-controls="panel-content-NavigationList-${navigationMenus.findIndex(m => m.id === activeSubmenu)}"]`) as HTMLElement;
        menuButton?.focus();
      }
      
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeSubmenu, navigationMenus]);
  
  const handleToggleSubmenu = (submenuId: string) => {
    setActiveSubmenu(activeSubmenu === submenuId ? null : submenuId);
  };

  const handleKeyDown = (event: React.KeyboardEvent, menu: any) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (menu.hasSubmenu) {
          handleToggleSubmenu(menu.id);
        } else {
          router.push(menu?.href as any ?? '/');
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (menu.hasSubmenu && activeSubmenu === menu.id) {
          const firstLink = submenuRef.current?.querySelector('a');
          firstLink?.focus();
        } else {
          const nextButton = event.currentTarget.parentElement?.nextElementSibling?.querySelector('button');
          nextButton?.focus();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        const prevButton = event.currentTarget.parentElement?.previousElementSibling?.querySelector('button');
        prevButton?.focus();
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (menu.hasSubmenu && activeSubmenu !== menu.id) {
          handleToggleSubmenu(menu.id);
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (activeSubmenu === menu.id) {
          setActiveSubmenu(null);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setActiveSubmenu(null);
        break;
    }
  };

  const handleSubmenuKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        const nextLink = (event.target as HTMLElement).closest('.link-wrapper')?.nextElementSibling?.querySelector('a') as HTMLElement;
        nextLink?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        const prevLink = (event.target as HTMLElement).closest('.link-wrapper')?.previousElementSibling?.querySelector('a') as HTMLElement;
        if (prevLink) {
          prevLink.focus();
        } else {
          const menuButton = (event.target as HTMLElement).closest('.panel-menu')?.querySelector('button') as HTMLElement;
          menuButton?.focus();
        }
        break;
      case 'Escape':
        event.preventDefault();
        setActiveSubmenu(null);
        const menuButton = (event.target as HTMLElement).closest('.panel-menu')?.querySelector('button') as HTMLElement;
        menuButton?.focus();
        break;
    }
  };
 
  return (
    <div className="navigation-bar-bg bg-white">
      <div className="navigation-bar" role="tabpanel" tabIndex={0} id="tabpanel-active">
        <div className="institutional-phrase-container sr-only">
          <div className="institutional">{t("areaSelector.bupiPortal")} </div>
        </div>
        
        <div className="flex flex-row justify-between w-full">
        
          <div className="flex items-center justify-center">
          <h1>
            <Link href="/">
              {logo?.url && <Image
                width={145}
                height={56} 
                alt={logo?.alt || 'BUPi - Balcão Único do Prédio'}
                loading="lazy" 
                className="w-[114px] h-[44px] md:w-[145px] md:h-[56px]" 
                src={logo?.url }
              /> }
            </Link>
          </h1>
          </div>
          
          {(isDesktop ) && (
            <nav className="internal-navigation-list py-16" aria-label="Navegação principal">
              <ul>
                {navigationMenus.map((menu, index) => (
                  <li key={index}>
                    <div 
                      ref={menu.id === activeSubmenu ? submenuRef : null} 
                      className="panel-menu internal-panel-navigation-list"
                    >
                      <AgoraButton
                        appearance='link'
                        aria-haspopup={menu.hasSubmenu ? "true" : undefined}
                        aria-expanded={menu.hasSubmenu ? activeSubmenu === menu.id : undefined}
                        aria-controls={menu.hasSubmenu ? `panel-content-NavigationList-${index}` : undefined}
                        className={`flex items-center justify-center agora-btn agora-btn-link-neutral agora-anchor agora-btn-with-icon panel-menu-button has-panel ${activeSubmenu === menu.id ? '' : 'collapsed'} !px-0 !py-[8px] internal-panel-navigation-list-btn`}
                        onClick={() => menu.hasSubmenu ? handleToggleSubmenu(menu.id) : router.push(menu?.href as any ?? '/')}
                        onKeyDown={(e) => handleKeyDown(e, menu)}
                        hasIcon={menu.hasSubmenu}
                        trailingIcon={activeSubmenu === menu.id ? 'agora-line-chevron-up' : 'agora-line-chevron-down'}
                      >
                        <span className="children-wrapper">{menu.title}</span>
                      </AgoraButton>
                      
                      <div 
                        className={`panel-menu-content !bg-[#F9F9FA] ${activeSubmenu === menu.id ? '' : 'collapsed'}`}
                        style={{ position: 'absolute', zIndex: 50 }}
                      >
                        <div className="panel-menu-content-children" id={`panel-content-NavigationList-${index}`}>
                          <div className="navigation-links-layout !bg-[#F9F9FA] pb-64">
                            <div className="container mx-auto text-xl-bold text-neutral-900">{menu.submenu?.subtitle}</div>
                            <div className="links !gap-x-32 !gap-y-16" role="list" aria-label="Voltar">
                              {(menu.submenu?.links || []).map((link: any, index: number) => (
                                <div key={index} className="link-wrapper" role="listitem">
                                  <span className="py-8 custom-header-link-wrapper">
                                    <BupiLink
                                      onClick={() => setActiveSubmenu(null)}
                                      onKeyDown={handleSubmenuKeyDown}
                                      label={link.text}
                                      url={link.href}
                                      target={link.target}
                                      className='py-8'
                                      hasIcon
                                    />
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="panel-menu-backdrop" onClick={() => setActiveSubmenu(null)}></div>
                      </div>
                    </div>
                  </li>
                ))}

                <li>
                  <BupiLinkButton
                    hasIcon
                    leadingIcon="agora-line-arrow-right-circle"
                    link={ctaButton?.url || ''}
                    label={ctaButton?.label || 'Plataforma'}
                    target={ctaButton?.target || "_blank"}
                  />
                </li>
              </ul>
            </nav>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default NavigationBar;
